import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, Trash2, ChevronLeft } from "lucide-react";
import { useBookmarks } from "../lib/bookmarks";

export default function MyGlossary() {
  const termsBag = useBookmarks("terms");
  const productsBag = useBookmarks("products");

  useEffect(() => {
    document.title = "Mon Glossaire — Beckyliss";
  }, []);

  const empty = termsBag.count === 0 && productsBag.count === 0;

  return (
    <div className="fade-in-up" data-testid="my-glossary-page">
      <section className="relative overflow-hidden hero-bg-gradient border-b border-bl-border">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-28">
          <Link to="/" className="inline-flex items-center gap-2 text-sm overline">
            <ChevronLeft size={14} /> Retour à l'accueil
          </Link>
          <div className="mt-8 max-w-2xl">
            <span className="overline flex items-center gap-2">
              <Heart size={12} strokeWidth={1.6} /> Ma sélection
            </span>
            <h1 className="font-serif font-light text-5xl md:text-6xl text-bl-text mt-3 leading-[1.05]">
              Mon <em className="italic text-bl-dore">Glossaire</em>.
            </h1>
            <p className="mt-6 text-base font-light text-bl-textSoft leading-relaxed">
              Retrouve ici les définitions et produits que tu as marqués comme
              favoris. Ta sélection est enregistrée sur cet appareil.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-20">
        {empty ? (
          <div className="text-center py-16 border border-bl-border" data-testid="empty-bookmarks">
            <Heart size={32} strokeWidth={1.2} className="mx-auto text-bl-textSoft" />
            <p className="font-serif text-2xl mt-6">Aucun favori pour le moment.</p>
            <p className="mt-4 text-sm text-bl-textSoft max-w-md mx-auto">
              Explore le glossaire et clique sur le ❤️ à côté d'un terme ou d'un
              produit pour l'ajouter à ta sélection.
            </p>
            <Link
              to="/"
              className="mt-8 inline-block border border-bl-mauveDeep px-6 py-3 text-sm hover:bg-bl-mauveDeep hover:text-white transition-colors"
            >
              Explorer le glossaire
            </Link>
          </div>
        ) : (
          <>
            {termsBag.count > 0 && (
              <div className="mb-16" data-testid="bookmarked-terms">
                <div className="flex items-baseline justify-between mb-8">
                  <h2 className="font-serif text-3xl md:text-4xl">
                    Termes sauvegardés
                  </h2>
                  <span className="overline">{termsBag.count} entrées</span>
                </div>
                <ul className="divide-y divide-bl-border">
                  {termsBag.items
                    .slice()
                    .sort((a, b) => (b.added_at || 0) - (a.added_at || 0))
                    .map((t) => (
                      <li key={t.slug} className="flex items-center justify-between py-5 gap-4">
                        <Link
                          to={`/terme/${t.slug}`}
                          className="flex-1 min-w-0 group"
                          data-testid={`bookmarked-term-${t.slug}`}
                        >
                          <span className="overline block">
                            {t.category_slug || "Glossaire"}
                          </span>
                          <span className="font-serif text-2xl block leading-tight mt-1 group-hover:text-bl-mauveDeep">
                            {t.name}
                          </span>
                        </Link>
                        <button
                          onClick={() => termsBag.remove(t.slug)}
                          aria-label="Retirer des favoris"
                          className="p-2 text-bl-textSoft hover:text-bl-fuchsia transition-colors"
                          data-testid={`remove-term-${t.slug}`}
                        >
                          <Trash2 size={16} strokeWidth={1.5} />
                        </button>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {productsBag.count > 0 && (
              <div data-testid="bookmarked-products">
                <div className="flex items-baseline justify-between mb-8">
                  <h2 className="font-serif text-3xl md:text-4xl">
                    Produits sauvegardés
                  </h2>
                  <span className="overline">{productsBag.count} pièces</span>
                </div>
                <ul className="divide-y divide-bl-border">
                  {productsBag.items
                    .slice()
                    .sort((a, b) => (b.added_at || 0) - (a.added_at || 0))
                    .map((p) => (
                      <li key={p.slug} className="flex items-center justify-between py-5 gap-4">
                        <Link
                          to={`/produit/${p.slug}`}
                          className="flex-1 min-w-0 group"
                          data-testid={`bookmarked-product-${p.slug}`}
                        >
                          <span className="overline block">
                            {p.category_slug || "Produit"}
                          </span>
                          <span className="font-serif text-2xl block leading-tight mt-1 group-hover:text-bl-mauveDeep">
                            {p.name}
                          </span>
                        </Link>
                        <button
                          onClick={() => productsBag.remove(p.slug)}
                          aria-label="Retirer des favoris"
                          className="p-2 text-bl-textSoft hover:text-bl-fuchsia transition-colors"
                          data-testid={`remove-product-${p.slug}`}
                        >
                          <Trash2 size={16} strokeWidth={1.5} />
                        </button>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
